<script>
import { mapActions, mapState } from "vuex";
import ContractNavigationWrapper from "@/components/ContractNavigationWrapper";
import DeleteContractFacilityModal from "@/pages/ContractFacilities/DeleteModal";

export default {
  // Included components
  components: {
    ContractNavigationWrapper,
    DeleteContractFacilityModal,
  },

  data() {
    return {
      // whether data is currently being fetched
      loading: true,
      contractFacilityId: null,
      name: null,
      contactEmail: null,
      handle: null,
    };
  },

  computed: {
    // Whether the form is being used to edit (vs. create)
    editing() {
      return this.contractFacilityId != null;
    },

    ...mapState(["user"]),
  },

  // When the component is created, fetch the worker (if in edit mode)
  // and get the pool and device options
  async created() {
    if (!this.user.isStaffAdmin) {
      await this.$router.push("/");
    }

    this.loading = true;
    this.contractFacilityId = this.$route.params.contractFacilityId;

    if (this.editing) {
      var contractFacility = await this.getContractFacility({
        contractFacilityId: this.contractFacilityId,
      });
      this.name = contractFacility.name;
      this.contactEmail = contractFacility.contact_email;
    }

    this.loading = false;
  },

  methods: {
    // On submit, either put or post the form data to the API,
    // depending on whether in edit or create mode
    async submit() {
      if (this.editing) {
        this.put();
      } else {
        this.post();
      }
    },

    // Update the worker via the API
    async put() {
      // Update given the form inputs
      const success = await this.putContractFacility({
        contractFacilityId: this.contractFacilityId,
        name: this.name,
        contact_email: this.contactEmail,
      });

      // If success, then redirect
      if (success) {
        await this.$router.push("/contract-facilities");
      }
    },

    // Post the new worker via the API
    async post() {
      // Update given the form inputs
      const success = await this.postContractFacility({
        name: this.name,
        contact_email: this.contactEmail,
        handle: this.handle,
      });

      // If success, then redirect
      if (success) {
        await this.$router.push("/contract-facilities");
      }
    },

    // Mapped vuex actions
    ...mapActions([
      "putContractFacility",
      "postContractFacility",
      "getContractFacility",
    ]),
  },
};
</script>

<template>
  <ContractNavigationWrapper>
    <template #breadcrumbs>
      <b-breadcrumb-item to="/contract-facilities">
        Contracts Facilities
      </b-breadcrumb-item>
      <b-breadcrumb-item v-if="editing" :to="`/contract-facilities`">
        {{ name != null ? name : "Contract Facility" }}
      </b-breadcrumb-item>
      <b-breadcrumb-item v-if="editing" active>Edit</b-breadcrumb-item>
      <b-breadcrumb-item v-else active>Add Contract Facility</b-breadcrumb-item>
    </template>
    <h1 class="mb-3">{{ editing ? "Edit" : "Add" }} Contract Facility</h1>
    <div v-if="!loading">
      <b-row>
        <b-col>
          <b-card class="mb-3">
            <b-form @submit.stop.prevent="submit">
              <b-row>
                <b-col>
                  <b-form-group
                    id="facility-name"
                    label="Contract Facility Name"
                    label-for="contractFacilityName"
                  >
                    <b-form-input
                      required
                      id="contractFacilityName"
                      v-model="name"
                      placeholder="Facility Name"
                      autocomplete="off"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="contact-name"
                    label="Contact Name"
                    label-for="contactName"
                  >
                    <b-form-input
                      required
                      id="contactName"
                      v-model="contactEmail"
                      placeholder="Contact Email"
                      autocomplete="off"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-form-group
                v-if="!editing"
                id="contract-facility-handle"
                label="Contract Facility Handle"
                label-for="contractFacility"
              >
                <b-form-input
                  required
                  id="contractFacilityHandle"
                  v-model="handle"
                  placeholder="Contract Facility Handle"
                  autocomplete="off"
                ></b-form-input>
                <small class="form-text text-muted">
                  Contract facility handles must be unique. They cannot be
                  edited once a contract has been created.
                </small>
              </b-form-group>
              <span>
                <div class="float-right">
                  <b-button type="submit" variant="primary">
                    {{ editing ? "Update" : "Add" }} Contract Facility
                  </b-button>
                  <b-button
                    v-if="editing"
                    class="ml-3 mr-3"
                    variant="danger"
                    v-b-modal.delete-modal
                  >
                    Delete
                  </b-button>
                  <DeleteContractFacilityModal
                    v-if="editing"
                    :contractFacilityId="contractFacilityId"
                    modal-id="delete-modal"
                  />
                </div>
              </span>
            </b-form>
          </b-card>
        </b-col>
      </b-row>
    </div>
    <div v-else>
      <b-row>
        <b-col class="text-center mt-5">
          <div class="mb-4">
            <b-spinner
              style="width: 4rem; height: 4rem"
              variant="primary"
              label="Loading Contract..."
            ></b-spinner>
          </div>
          <b>Loading Contract...</b>
        </b-col>
      </b-row>
    </div>
  </ContractNavigationWrapper>
</template>

<style>
#toggle-btn {
  padding: 7px 12px;
  margin-top: -1px;
}
</style>
